// data/services.js
import {
    FaCode,
    FaReact,
    FaShoppingCart,
    FaMobileAlt,
    FaSearch,
    FaServer,
    FaPalette,
    FaChartLine,
    FaCloud,
    FaUsersCog
} from 'react-icons/fa';

const services = [
    {
        slug: 'saas-crm-development',
        icon: FaUsersCog,
        title: 'SaaS & CRM Development',
        description: 'Multi-tenant SaaS platforms and custom CRMs built around your workflow',
        features: ['Multi-Tenant Architecture', 'Subscription Billing', 'Role-Based Access', 'Dashboards & Reports'],
        overview: 'We build CRMs and SaaS products that run real businesses: consultancies, real estate agencies and fleet operators. From lead capture to invoicing, every module is designed around how your team actually works, with secure tenant isolation when you sell it to many companies.',
        includes: [
            'Requirement workshops and module planning',
            'Multi-tenant database design with strict data isolation',
            'Subscription billing with Stripe or Paddle, free trials and usage limits',
            'Role, department and branch-level permissions',
            'Lead Ads integrations (Facebook, Instagram, TikTok)',
            'Invoicing, expenses, attendance and HR modules',
            'Analytics dashboards, PDF and Excel exports',
            'Automated tests for security-critical access rules',
        ],
        technologies: ['Next.js', 'React', 'TypeScript', 'Node.js', 'Laravel', 'MySQL', 'Stripe', 'Paddle'],
        relatedProjects: ['consultants-page', 'euroworld-crm', 'daehanskava', 'nexfleet'],
        popular: true
    },
    {
        slug: 'custom-web-development',
        icon: FaCode,
        title: 'Custom Web Development',
        description: 'Bespoke websites built from scratch using latest technologies',
        features: ['HTML5/CSS3', 'JavaScript ES6+', 'Responsive Design', 'Cross-browser Compatible'],
        overview: 'A website designed and coded specifically for your business, not a template. We focus on clean code, speed and a design that turns visitors into customers.',
        includes: [
            'Custom design matched to your brand',
            'Fully responsive layouts for mobile, tablet and desktop',
            'Contact and lead forms with email notifications',
            'On-page SEO and fast loading',
            'Cross-browser testing',
            'Deployment to your hosting and domain',
        ],
        technologies: ['HTML5', 'CSS3', 'JavaScript', 'Bootstrap', 'SCSS', 'jQuery'],
        relatedProjects: ['osy-solutions', 'daehan-links', 'euroworld-education'],
    },
    {
        slug: 'react-nextjs-applications',
        icon: FaReact,
        title: 'React.js & Next.js Applications',
        description: 'High-performance single page and server-rendered applications',
        features: ['React 19 / Next.js', 'Redux Toolkit', 'TypeScript', 'SSR / ISR'],
        overview: 'Fast, interactive web applications built with React and Next.js. With server-side rendering and incremental static regeneration your pages load quickly and rank well on Google.',
        includes: [
            'Next.js App Router or React SPA architecture',
            'State management with Redux Toolkit',
            'TypeScript for reliable, maintainable code',
            'SSR, SSG and ISR for performance and SEO',
            'API integration and authentication',
            'Reusable component library',
        ],
        technologies: ['React', 'Next.js', 'TypeScript', 'Redux Toolkit', 'Tailwind', 'Vitest'],
        relatedProjects: ['apna-ashiyana', 'consultants-page', 'universities-page'],
        popular: true
    },
    {
        slug: 'ecommerce-development',
        icon: FaShoppingCart,
        title: 'E-commerce Development',
        description: 'Complete online stores with payment integration',
        features: ['Product Catalogue', 'Payment Gateway', 'Inventory Management', 'Order Tracking'],
        overview: 'Online stores that are easy to manage and easy to buy from. We set up your catalogue, cart, checkout and payment gateway so you can start selling online quickly.',
        includes: [
            'Product catalogue with categories and filters',
            'Cart and secure checkout',
            'Stripe and local payment gateway integration',
            'Inventory and order management',
            'Order tracking and email notifications',
            'Admin dashboard for sales reports',
        ],
        technologies: ['Laravel', 'React', 'Next.js', 'MySQL', 'Stripe'],
        relatedProjects: ['dr-performance'],
    },
    {
        slug: 'backend-api-development',
        icon: FaServer,
        title: 'Backend & API Development',
        description: 'Robust server-side solutions and APIs',
        features: ['Node.js / Laravel', 'REST APIs', 'Database Design', 'Cloud Integration'],
        overview: 'Secure, scalable backends and APIs that power your web and mobile apps. We design the database, write the business logic and integrate the third-party services you need.',
        includes: [
            'REST API design and documentation',
            'JWT, Sanctum and Passport authentication',
            'MySQL / PostgreSQL schema design and query optimization',
            'Third-party integrations (Stripe, Firebase, Twilio, Meta APIs)',
            'File storage with AWS S3 and Cloudinary',
            'Cron jobs, queues and background tasks',
        ],
        technologies: ['Node.js', 'Laravel', 'PHP', 'CodeIgniter', 'MySQL', 'PostgreSQL', 'AWS S3'],
        relatedProjects: ['euroworld-crm', 'student-application-crm', 'nexfleet'],
    },
    {
        slug: 'responsive-web-design',
        icon: FaMobileAlt,
        title: 'Responsive Web Design',
        description: 'Mobile-first designs that work on all devices',
        features: ['Mobile Optimization', 'Tablet Friendly', 'Touch Interactions', 'Fast Loading'],
        overview: 'Most of your visitors are on their phones. We make sure your website looks and works great on every screen size.',
        includes: [
            'Mobile-first layouts',
            'Touch-friendly navigation and interactions',
            'Image optimization and lazy loading',
            'Testing on real devices and browsers',
        ],
        technologies: ['HTML5', 'CSS3', 'SCSS', 'Bootstrap', 'Tailwind'],
        relatedProjects: ['osy-solutions', 'daehan-links'],
    },
    {
        slug: 'ui-ux-design',
        icon: FaPalette,
        title: 'UI/UX Design',
        description: 'User-centered designs that enhance engagement',
        features: ['Wireframing', 'Prototyping', 'User Testing', 'Design Systems'],
        overview: 'Good design makes your product easy to use. We plan user journeys, create wireframes and build polished interfaces that your users will enjoy.',
        includes: [
            'User flows and wireframes',
            'High-fidelity designs in Figma',
            'Clickable prototypes',
            'Design systems and reusable components',
        ],
        technologies: ['Figma', 'Adobe XD', 'HTML', 'CSS'],
        relatedProjects: ['consultants-page', 'apna-ashiyana'],
    },
    {
        slug: 'seo-optimization',
        icon: FaSearch,
        title: 'SEO Optimization',
        description: 'Built-in SEO for better search engine rankings',
        features: ['On-page SEO', 'Speed Optimization', 'Meta Tags', 'Schema Markup'],
        overview: 'Get found on Google. We optimize your site structure, content and speed so search engines and customers can find you.',
        includes: [
            'Keyword research and on-page optimization',
            'Meta tags, Open Graph and schema markup',
            'Dynamic sitemaps and robots configuration',
            'Core Web Vitals and speed optimization',
            'Google Search Console setup',
        ],
        technologies: ['Next.js', 'Schema.org', 'Google Search Console', 'Lighthouse'],
        relatedProjects: ['apna-ashiyana', 'euroworld-education'],
    },
    {
        slug: 'deployment-devops',
        icon: FaCloud,
        title: 'Deployment & Hosting',
        description: 'Reliable deployment on VPS, cloud and shared hosting',
        features: ['Linux Servers', 'CI/CD', 'SSL & Domains', 'Backups'],
        overview: 'We take your project live and keep it running, whether on a VPS, cloud provider or cPanel hosting.',
        includes: [
            'Server setup with Apache or Nginx',
            'CI/CD with GitHub Actions',
            'Domain, DNS and SSL configuration',
            'Automated backups and monitoring',
        ],
        technologies: ['Linux', 'Nginx', 'Apache', 'GitHub Actions', 'cPanel'],
        relatedProjects: ['consultants-page', 'nexfleet'],
    },
    {
        slug: 'website-maintenance',
        icon: FaChartLine,
        title: 'Website Maintenance',
        description: 'Ongoing support and updates for your website',
        features: ['Security Updates', 'Backup Services', 'Performance Monitoring', 'Content Updates'],
        overview: 'Your website needs care after launch. We keep it secure, updated and fast so you can focus on your business.',
        includes: [
            'Security patches and dependency updates',
            'Regular backups',
            'Uptime and performance monitoring',
            'Content and feature updates',
            'Bug fixes and priority support',
        ],
        technologies: ['Laravel', 'React', 'Next.js', 'MySQL'],
        relatedProjects: [],
    }
];

export const getServiceBySlug = (slug) => services.find((service) => service.slug === slug);

export default services;
